import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShieldCheck, ChevronDown, UserCircle2, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { useCurrentUser, useLogout } from '@/hooks/useAuth';
import { useRealtime } from '@/hooks/useRealtime';
import { useEffectiveWorkspaceId } from '@/hooks/useWorkspaceContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NotificationBell } from '@/components/NotificationBell';
import { UnverifiedEmailBanner } from '@/components/layout/UnverifiedEmailBanner';

interface NavItem {
  to: string;
  labelKey: string;
}

interface NavGroup {
  headingKey: string;
  items: NavItem[];
}

export function AppShell({
  titleKey,
  children,
}: {
  titleKey: string;
  children: ReactNode;
}) {
  const { wsId: paramWsId = '' } = useParams<{ wsId: string }>();
  // Persist + fall back to the last active workspace so account-level pages
  // (e.g. /app/profile) that lack a :wsId param still render valid nav links
  // instead of /app/workspace//… (REVIEW-CORRECT C3).
  const wsId = useEffectiveWorkspaceId(paramWsId);
  const location = useLocation();
  const { t } = useTranslation();
  const me = useCurrentUser();
  const logout = useLogout();
  // Establish the singleton realtime socket for the session — the
  // NotificationBell badge updates live; no 60s polling needed.
  useRealtime();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile sidebar on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? 'hidden' : original;
    return () => {
      document.body.style.overflow = original;
    };
  }, [mobileOpen]);

  const isAdmin = me.data?.role === 'owner' || me.data?.role === 'admin';

  // Grouped IA (NUMBER_ONE_PLAN W3): a flat 17-item list made it hard to
  // know where to go next. Group by intent — your daily flow (Work), the
  // database you act on (Records), the AI surfaces (Intelligence), and
  // config (Workspace). Admin-only links live in Workspace, appended
  // conditionally so members don't see them (each page also gates content).
  const workspaceItems: NavItem[] = [];
  if (isAdmin) {
    workspaceItems.push(
      { to: `/app/workspace/${wsId}/connectors`, labelKey: 'app.nav.connectors' },
      { to: `/app/workspace/${wsId}/assignment-rules`, labelKey: 'app.nav.assignment_rules' },
    );
  }
  workspaceItems.push(
    { to: `/app/workspace/${wsId}/audit-log`, labelKey: 'app.nav.audit' },
    { to: `/app/workspace/${wsId}/shared-links`, labelKey: 'app.nav.shared_links' },
    { to: `/app/workspace/${wsId}/settings`, labelKey: 'app.nav.settings' },
  );

  const groups: NavGroup[] = [
    {
      headingKey: 'app.nav_group.work',
      items: [
        { to: `/app/workspace/${wsId}/feed`, labelKey: 'app.nav.feed' },
        { to: `/app/workspace/${wsId}/inbox`, labelKey: 'app.nav.inbox' },
        { to: `/app/workspace/${wsId}/deals`, labelKey: 'app.nav.deals' },
        { to: `/app/workspace/${wsId}/sequences`, labelKey: 'app.nav.sequences' },
        { to: `/app/workspace/${wsId}/approvals`, labelKey: 'app.nav.approvals' },
      ],
    },
    {
      headingKey: 'app.nav_group.records',
      items: [
        { to: `/app/workspace/${wsId}/people`, labelKey: 'app.nav.people' },
        { to: `/app/workspace/${wsId}/companies`, labelKey: 'app.nav.companies' },
        { to: `/app/workspace/${wsId}/meishi`, labelKey: 'app.nav.meishi' },
        { to: `/app/workspace/${wsId}/bulk-import`, labelKey: 'app.nav.bulk_import' },
      ],
    },
    {
      headingKey: 'app.nav_group.intelligence',
      items: [
        { to: `/app/workspace/${wsId}/agents`, labelKey: 'app.nav.agents' },
        { to: `/app/workspace/${wsId}/ai-results`, labelKey: 'app.nav.ai_results' },
        { to: `/app/workspace/${wsId}/reports`, labelKey: 'app.nav.reports' },
      ],
    },
    {
      headingKey: 'app.nav_group.workspace',
      items: workspaceItems,
    },
  ];

  /**
   * Active-state policy:
   *  - `/app/workspace/:wsId` (the workspace root) is also Feed.
   *  - For every other nav item, `startsWith(to)` is correct because
   *    each route is a unique leaf — no item is a prefix of another.
   */
  function isActive(to: string): boolean {
    const path = location.pathname;
    if (to.endsWith('/feed')) {
      const root = to.replace(/\/feed$/, '');
      return path === root || path.startsWith(to);
    }
    return path.startsWith(to);
  }

  return (
    <div className="min-h-screen flex bg-ink-50">
      {/* Mobile top bar — present only below md */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-ink-200 flex items-center justify-between px-4">
        <Link to="/app" className="flex items-center gap-2 text-ink-900 font-semibold">
          <img src="/logo.png" alt="" width={18} height={18} />
          <span className="serif-jp text-base">{t('brand.name')}</span>
        </Link>
        <div className="flex items-center gap-1">
          <LanguageSwitcher compact />
          <NotificationBell />
          <button
            type="button"
            aria-label={mobileOpen ? t('nav.close_menu') : t('nav.open_menu')}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-ink-700 hover:bg-ink-100"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Sidebar — desktop persistent, mobile slide-in */}
      <AnimatePresence>
        {(mobileOpen || true) && (
          <motion.aside
            key="sidebar"
            initial={false}
            animate={{
              x: mobileOpen ? 0 : 0,
            }}
            className={
              'w-56 shrink-0 border-r border-ink-200 bg-white flex flex-col fixed md:sticky top-0 inset-y-0 z-40 transition-transform ' +
              (mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
            }
            style={{ height: '100vh' }}
          >
            <div className="px-5 py-4 border-b border-ink-200 hidden md:flex items-center gap-2">
              <Link to="/app" className="flex items-center gap-2 text-ink-900 font-semibold">
                <img src="/logo.png" alt="" width={18} height={18} />
                <span className="serif-jp text-base">{t('brand.name')}</span>
              </Link>
            </div>
            <nav className="flex-1 px-2 py-3 text-sm overflow-y-auto md:mt-0 mt-14">
              {groups.map((group, gi) => (
                <div key={group.headingKey} className={gi > 0 ? 'mt-4' : ''}>
                  <p className="px-3 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-ink-400">
                    {t(group.headingKey)}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((it) => (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        className={() =>
                          'block px-3 py-1.5 rounded-md transition ' +
                          (isActive(it.to)
                            ? 'bg-accent-50 text-accent-700 font-medium'
                            : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900')
                        }
                      >
                        {t(it.labelKey)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}

              {/* Admin is owner/admin-only — accessed via the UserMenu
                  in the top header, not the sidebar. The sidebar is
                  workspace-scoped navigation; the UserMenu is
                  identity-scoped. */}
            </nav>
            {/* Identity + locale + sign-out all live in the UserMenu
                in the top header now. Keeping the sidebar footer
                empty preserves the floor so the nav doesn't bleed off
                the bottom of the viewport. */}
            <div className="border-t border-ink-200 px-3 py-3 text-[11px] text-ink-400 truncate">
              {me.data?.email}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/30"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-x-auto pt-14 md:pt-0">
        <header className="hidden md:block border-b border-ink-200 bg-white">
          <div className="px-8 py-4 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-ink-900">{t(titleKey)}</h1>
            <div className="flex items-center gap-2">
              <LanguageSwitcher compact />
              <NotificationBell />
              <UserMenu
                user={me.data ?? null}
                isAdmin={isAdmin}
                wsId={wsId}
                onLogout={logout}
              />
            </div>
          </div>
        </header>
        <UnverifiedEmailBanner />
        <div className="px-4 md:px-8 py-6">{children}</div>
      </main>
    </div>
  );
}

function UserMenu({
  user,
  isAdmin,
  wsId,
  onLogout,
}: {
  user: { id: string; name: string | null; email: string | null; pictureUrl: string | null } | null;
  isAdmin: boolean;
  wsId?: string;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest('[data-patronez-usermenu]');
      if (!el) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!user) return null;
  const initials = (() => {
    const n = (user.name || user.email || '?').trim();
    const parts = n.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  })();

  return (
    <div data-patronez-usermenu className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-ink-100 transition"
        aria-label={t('common.user_menu', { defaultValue: 'Account menu' })}
      >
        {user.pictureUrl ? (
          <img src={user.pictureUrl} alt="" className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-ink-900 text-white text-[11px] font-semibold">
            {initials}
          </span>
        )}
        <ChevronDown className="h-3 w-3 text-ink-500" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-xl border border-ink-200 bg-white shadow-lg overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-ink-100">
            <div className="text-[13.5px] font-semibold text-ink-900 truncate">{user.name || '—'}</div>
            <div className="text-[12px] text-ink-500 truncate">{user.email}</div>
          </div>
          <ul className="py-1 text-[13px]">
            <li>
              <Link to="/app/profile" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-ink-700 hover:bg-ink-50 hover:text-ink-900">
                <UserCircle2 className="h-3.5 w-3.5 text-ink-500" />
                {t('nav.profile', { defaultValue: 'Profile' })}
              </Link>
            </li>
            {wsId && (
              <li>
                <Link to={`/app/workspace/${wsId}/settings`} onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-ink-700 hover:bg-ink-50 hover:text-ink-900">
                  <SettingsIcon className="h-3.5 w-3.5 text-ink-500" />
                  {t('nav.settings', { defaultValue: 'Settings' })}
                </Link>
              </li>
            )}
            {isAdmin && (
              <li>
                <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-ink-700 hover:bg-ink-50 hover:text-ink-900">
                  <ShieldCheck className="h-3.5 w-3.5 text-ink-500" />
                  {t('nav.admin', { defaultValue: 'Admin' })}
                </Link>
              </li>
            )}
            <li><div className="my-1 h-px bg-ink-100" /></li>
            <li>
              <button
                type="button"
                onClick={() => { setOpen(false); onLogout(); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-3.5 w-3.5" />
                {t('common.logout', { defaultValue: 'Sign out' })}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
